import React from "react";
import { useEffect, useMemo, useRef, useState } from 'react';
import { mergeClassNames } from '../../../../../../../../utils/commonFunctions';
import { CheckBoxSvg, FilterSvg, SearchSvg } from '../../../../../../../CommonComponents/svgs/Svgs';
import '../../styles/ThermalFilters.scss';

interface ThermalFilterOption {
  id: string;
  label: string;
  checked: boolean;
  disabled?: boolean;
}

interface ThermalFilterCategory {
  id: string;
  name: string;
  selected: boolean;
  options: ThermalFilterOption[];
}

interface ThermalFiltersProps {
  active: boolean;
  disabled?: boolean;
  className?: string;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  categories: ThermalFilterCategory[];
  viewFiltersTitle: string;
  onActiveChange: () => void;
  onSelectCategory: (categoryId: string) => void;
  onToggleOption: (categoryId: string, optionId: string) => void;
  onToggleAllOptions: (categoryId: string) => void;
}

const ThermalFilters = ({
  active,
  onActiveChange,
  className,
  placement = 'bottom',
  disabled = false,
  categories,
  viewFiltersTitle,
  onSelectCategory,
  onToggleOption,
  onToggleAllOptions,
}: ThermalFiltersProps) => {
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedCategory = categories.find((item) => item.selected);
  const filterApplied = categories.flatMap((item) => item.options).filter((opt) => opt.checked).length;

  const allOptionsSelected = useMemo(
    () => selectedCategory?.options.length === selectedCategory?.options.filter((o) => o.checked).length,
    [selectedCategory],
  );
  const noOptionsSelected = useMemo(() => selectedCategory?.options.filter((o) => o.checked).length === 0, [selectedCategory]);

  const allChecked = useMemo(() => (noOptionsSelected ? 'unchecked' : allOptionsSelected ? 'checked' : 'indeterminate'), [selectedCategory]);

  useEffect(() => {
    if (!active) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        onActiveChange();
      }
    };

    document.addEventListener('click', handleClickOutside, true);
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [active, onActiveChange]);

  return (
    <div ref={containerRef} className={mergeClassNames('thermal__filter', active && '__active', className)}>
      <button className="__trigger" disabled={disabled} onClick={() => onActiveChange()}>
        <div className="__trigger__icon">
          <FilterSvg />
        </div>
        {filterApplied > 0 && !disabled && (
          <div className="__trigger__details">
            <div className="__badge">{filterApplied}</div>
            <span>Filters</span>
          </div>
        )}
      </button>
      <div className={mergeClassNames('__dialouge', placement)} onClick={(e) => e.stopPropagation()}>
        <div className="__header">
          <FilterSvg />
          <span>Filters</span>
        </div>
        <div className="__body">
          <div className="__section__stack">
            <div className="__section">
              <span className="__title">Thermal Filters</span>
              <div className="__card__container">
                {categories.filter((parent) => parent.id === 'sections').map((parent) => {
                  const selected = parent.selected ? '__selected' : '';
                  return (
                    <button
                      key={parent.id}
                      className={mergeClassNames('__task__card', selected)}
                      onClick={() => onSelectCategory(parent.id)}
                    >
                      <span className="__task__title">
                        {parent.name}
                      </span>
                      <div className="__badge">{parent.options.filter((o) => o.checked).length}</div>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="__seperator horizontal"></div>
            <div className="__section">
              <span className="__title">{viewFiltersTitle}</span>
              <div className="__card__container">
                {categories.filter((parent) => parent.id !== 'sections').map((parent) => {
                  const selected = parent.selected ? '__selected' : '';
                  return (
                    <button
                      key={parent.id}
                      className={mergeClassNames('__task__card', selected)}
                      onClick={() => onSelectCategory(parent.id)}
                    >
                      <span className="__task__title">
                        {parent.name}
                      </span>
                      <div className="__badge">{parent.options.filter((o) => o.checked).length}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="__seperator"></div>
          <div className="__section large">
            <div className="__search__container">
              <div className="__search">
                <SearchSvg />
                <input
                  type="text"
                  placeholder="Search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
            <div className="__card__container">
              <button
                className={mergeClassNames('__task__card', allOptionsSelected ? '__selected' : '')}
                onClick={() => {
                  if (selectedCategory) onToggleAllOptions(selectedCategory.id);
                }}
              >
                <CheckBoxSvg checked={allChecked} />
                <span className="__task__title">
                  All
                </span>
              </button>
              {selectedCategory?.options
                .filter((option) => option.label.toLowerCase().includes(search.toLowerCase()))
                .map((option) => {
                  const selected = option.checked ? '__selected' : '';
                  return (
                    <button
                      key={option.id}
                      className={mergeClassNames('__task__card', selected)}
                      disabled={option.disabled}
                      onClick={() => {
                        onToggleOption(selectedCategory.id, option.id);
                      }}
                    >
                      <CheckBoxSvg checked={selected ? 'checked' : 'unchecked'} />
                      <span className="__task__title">
                        {option.label}
                      </span>
                    </button>
                  );
                })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(ThermalFilters);
